import {Injectable} from '@angular/core';
import {map} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {StatusApiModel} from "../models/status.api.model";
import {ProjectApiModel} from "../models/project.api-model";
import {ResultListApiModel} from "../models/result-list.api.model";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import {environment} from "../../../environments/environment";
import {classToPlain, plainToClass, plainToClassFromExist} from "class-transformer";
import {QueryParamsApiModel} from "../models/query-params-api.model";
import {QueryParamsService} from "../../core/services/query-params.service";
import {ProjectInstagramMediaResponseApiModel} from "../models/project-instagram-media-response.api-model";
import {ReactionApiModel} from "../models/reaction.api.model";
import {FollowApiModel} from "../models/follow.api-model";
import {UserApiModel} from "../models/user.api-model";

@Injectable()
export class ProjectRestService {
  private readonly resource: string = '/projects';
  public statuses: StatusApiModel[];

  constructor(
    private http: HttpClient,
    private params: QueryParamsService
  ) {
  }

  public create(projectAM: ProjectApiModel): Observable<ProjectApiModel> {
    return this.http.post<ProjectApiModel>(environment.apiUrl + this.resource, projectAM)
      .pipe(map((result: ProjectApiModel) => plainToClass(ProjectApiModel, result)));
  }

  public put(projectAM: ProjectApiModel): Observable<ProjectApiModel> {
    return this.http.put<ProjectApiModel>(environment.apiUrl + projectAM['@id'], classToPlain(projectAM))
      .pipe(map((result: ProjectApiModel) => plainToClass(ProjectApiModel, result)));
  }

  public downloadExternalHistory(projectAM: ProjectApiModel, options?: {headers?: HttpHeaders}): Observable<ProjectApiModel> {
    const {vin, registrationPlate, firstRegistrationInCountry} = projectAM.attributes;
    return this.http.put<ProjectApiModel>(environment.apiUrl + projectAM['@id'] + '/external-history', {vin, registrationPlate, firstRegistrationInCountry}, options)
      .pipe(map((result: ProjectApiModel) => plainToClass(ProjectApiModel, result)));
  }

  public deleteExternalHistory(projectAM: ProjectApiModel, options?: {headers?: HttpHeaders}): Observable<ProjectApiModel> {
    return this.http.delete<ProjectApiModel>(environment.apiUrl + projectAM['@id'] + '/external-history', options)
      .pipe(map((result: ProjectApiModel) => plainToClass(ProjectApiModel, result)));
  }

  public get(id: string, options?: {headers?: HttpHeaders}): Observable<ProjectApiModel> {
    return this.http.get<ProjectApiModel>(environment.apiUrl + this.resource + '/' + id, options)
      .pipe(map((result: ProjectApiModel) => plainToClass(ProjectApiModel, result)));
  }

  public getCollection(queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel()): Observable<ResultListApiModel<ProjectApiModel>> {
    const params = this.params.applyParameters(queryParamsApiModel);
    return this.http.get<ResultListApiModel<ProjectApiModel>>(environment.apiUrl + this.resource, {params})
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<ProjectApiModel>(ProjectApiModel), result as object)));
  }

  public getRatedCollection(queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel(), category: string = 'overall'): Observable<ResultListApiModel<ProjectApiModel>> {
    let params = this.params.applyParameters(queryParamsApiModel);
    params = params.append('category', category);
    return this.http.get<ResultListApiModel<ProjectApiModel>>(environment.apiUrl + '/rankings/projects', {params})
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<ProjectApiModel>(ProjectApiModel), result as object)));
  }

  public getUserSubResourceCollection(userId: string): Observable<ResultListApiModel<ProjectApiModel>> {
    return this.http.get<ResultListApiModel<ProjectApiModel>>(environment.apiUrl + userId + this.resource)
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<ProjectApiModel>(ProjectApiModel), result as object)));
  }

  public getReceivedReactionsSubResourceCollection(projectId: string, queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel()): Observable<ResultListApiModel<ReactionApiModel>> {
    const params = this.params.applyParameters(queryParamsApiModel);
    return this.http.get<ResultListApiModel<ReactionApiModel>>(environment.apiUrl + projectId + '/received_reactions', {params})
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<ReactionApiModel>(ReactionApiModel), result as object)));
  }

  public getReceivedFollowsSubResourceCollection(projectId: string, queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel()): Observable<ResultListApiModel<FollowApiModel>> {
    const params = this.params.applyParameters(queryParamsApiModel);
    return this.http.get<ResultListApiModel<FollowApiModel>>(environment.apiUrl + projectId + '/received_follows', {params})
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<FollowApiModel>(FollowApiModel), result as object)));
  }

  // public getEquipmentSubResourceCollection(projectId: string): Observable<ResultListApiModel<ProjectApiModel>> {
  //   return this.http.get<ResultListApiModel<ProjectApiModel>>(environment.apiUrl + projectId + this.resource)
  //     .pipe(map(result => plainToClassFromExist(new ResultListApiModel<ProjectApiModel>(ProjectApiModel), result as object)));
  // }

  public getStatusCollection(): Observable<ResultListApiModel<StatusApiModel>> {
    return this.http.get<ResultListApiModel<StatusApiModel>>(environment.apiUrl+ '/statuses')
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<StatusApiModel>(StatusApiModel), result as object)));
  }

  public changeOwner(projectAM: ProjectApiModel, ownerInput: {email?: string | null, user?: string, from: Date, pin: string}) {
    return this.http.put<ProjectApiModel>(environment.apiUrl + projectAM['@id'] +  '/change-main-owner', ownerInput)
      .pipe(map((result: ProjectApiModel) => plainToClass(ProjectApiModel, result)));
  }

  public findOwnerCandidates(projectSlug: string, query: string): Observable<UserApiModel[]> {
    const params = new HttpParams().set('q', query);
    return this.http.get<{candidates: UserApiModel[]}>(`${environment.apiUrl}/projects/${projectSlug}/owner-candidate`, {params})
      .pipe(map(result => result.candidates));
  }

  public syncInstagram(projectSlug: string): Observable<{status: string; queued: boolean}> {
    return this.http.post<{status: string; queued: boolean}>(`${environment.apiUrl}/projects/${projectSlug}/instagram/sync`, {});
  }

  public getInstagramMedia(projectSlug: string): Observable<ProjectInstagramMediaResponseApiModel> {
    return this.http.get<ProjectInstagramMediaResponseApiModel>(`${environment.apiUrl}/projects/${projectSlug}/instagram/media`)
      .pipe(map((result: ProjectInstagramMediaResponseApiModel) => plainToClass(ProjectInstagramMediaResponseApiModel, result)));
  }

  public getInstagramSelections(projectSlug: string): Observable<{instagramMediaIds: string[]}> {
    return this.http.get<{instagramMediaIds: string[]}>(`${environment.apiUrl}/projects/${projectSlug}/instagram/selections`);
  }

  public putInstagramSelections(projectSlug: string, instagramMediaIds: string[]): Observable<{instagramMediaIds: string[]}> {
    return this.http.put<{instagramMediaIds: string[]}>(`${environment.apiUrl}/projects/${projectSlug}/instagram/selections`, {instagramMediaIds});
  }

  updateProjectStatus(project: any, status: number) {
    const request = {
      status: status,
    };
    // return this.putProject(project, request);
  }

  toggleLockProject(project: any) {
    const request = {
      locked: !project.locked,
    };
    // return this.putProject(project, request);
  }
}
